import { useEffect, useRef, useState, useCallback } from 'react'
import { useConciergeStore } from '@/stores/useConciergeStore'
import { streamConcierge } from '@/lib/api'
import { ConciergeChatMessage } from './ConciergeChatMessage'
import { ConciergeChatInput } from './ConciergeChatInput'
import { ConciergeThinkingBlock } from './ConciergeThinkingBlock'
import { ConciergeToolPill } from './ConciergeToolPill'
import { ConciergeModeBanner } from './ConciergeModeBanner'
import type { PendingAttachment } from '../attachments'

/**
 * The chat surface: scrollable message list + sticky input, with the
 * live streaming state for the in-flight assistant turn rendered as an
 * inline block above the input.
 *
 * Auto-load behavior on mount:
 *   - Fetch the user's session list.
 *   - If we have a persisted sessionId, hydrate its turns so a refresh
 *     picks up where the user left off. Otherwise start fresh — the
 *     backend lazy-creates a new session on the first POST.
 */
export function ConciergeChat() {
  const {
    sessionId,
    turns,
    chatMode,
    modeContext,
    autoGreetArmed,
    workflowStatus,
    streamingThinking,
    streamingToolCalls,
    streamingContent,
    error,
    walkActive,
    loadSessions,
    loadSession,
    appendUserTurn,
    startStreaming,
    appendThinking,
    finishThinking,
    registerToolCall,
    registerToolResult,
    appendStreamingContent,
    finishStreaming,
    setError,
    exitMode,
    advanceWalk,
    disarmAutoGreet,
  } = useConciergeStore()

  // Terminal wrap-up tools that should advance the walk to the next
  // attention-queue block. Mirrors the Obsidian plugin's `_advanceWalk`
  // trigger set EXACTLY: archive_block, reschedule_block, archive_project.
  // capture_block_outcome (scoring) is NOT terminal — after scoring, the
  // agent typically wants to keep working (read more files, update
  // project notes, propose follow-ups, then archive). Including
  // capture_block_outcome here would whisk the modal to the next block
  // mid-turn and orphan the agent's close-out work.
  const WALK_TERMINAL_TOOLS = new Set([
    'archive_block',
    'reschedule_block',
    'archive_project',
  ])

  // Two-phase advance trigger, mirrors Obsidian:
  //   1. onToolResult: if a terminal tool succeeded, flag this turn.
  //   2. onComplete: if the flag is set, advance to the next walk target.
  // This ensures the agent's FULL turn completes (all tool calls + final
  // text rendered) before we transition. Firing mid-turn (the old setTimeout
  // pattern) cut off post-score work like update_project_notes + archive.
  const terminalActionFiredRef = useRef(false)
  function noteTerminalToolResult(toolName: string, ok: boolean) {
    if (!walkActive || chatMode !== 'wrapup') return
    if (!ok) return
    if (!WALK_TERMINAL_TOOLS.has(toolName)) return
    terminalActionFiredRef.current = true
  }
  function maybeAdvanceOnComplete() {
    if (!terminalActionFiredRef.current) return
    terminalActionFiredRef.current = false
    if (!walkActive || chatMode !== 'wrapup') return
    void advanceWalk()
  }

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Composer state is owned here so option / form / multiselect cards can
  // stage their answer text directly into the chat input. Click-to-stage
  // (rather than click-to-send) gives the user a chance to edit the
  // pre-filled text or add context before hitting Enter — they can still
  // press Enter immediately for the fast flow.
  //
  // Append-or-replace contract: when an assistant turn renders multiple
  // option cards (e.g. "Project home?" + "Output?" in one turn), the user
  // expects each click to ACCUMULATE — not wipe the prior pick. Cards
  // pass their `replacePrevious` (their own last-staged text) so a card
  // can update its own contribution in place across re-picks while still
  // appending to other cards' contributions. When `replacePrevious` is
  // present in the composer, it's swapped in place; otherwise the new
  // text is appended after a `; ` separator.
  const [composerValue, setComposerValue] = useState('')
  const [composerFocusSignal, setComposerFocusSignal] = useState(0)
  const stageInComposer = useCallback(
    (text: string, replacePrevious?: string) => {
      setComposerValue((curr) => {
        const trimmedCurr = curr.trim()
        if (replacePrevious && curr.includes(replacePrevious)) {
          // Card is updating its own previous pick — swap in place so
          // the rest of the composer (other cards' contributions, any
          // freeform text the user typed) isn't disturbed.
          return curr.replace(replacePrevious, text)
        }
        if (!trimmedCurr) return text
        return `${trimmedCurr}; ${text}`
      })
      setComposerFocusSignal((n) => n + 1)
    },
    [],
  )

  // First-mount: load sessions list + rehydrate active session if any.
  useEffect(() => {
    loadSessions()
    if (sessionId) {
      loadSession(sessionId)
    }
    // Intentionally empty — only on mount. sessionId is read from initial
    // store state; rehydration is a one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-scroll to the bottom whenever anything changes in the chat. Use
  // `instant` while streaming so the page follows the text smoothly.
  useEffect(() => {
    const isStreaming = workflowStatus === 'streaming'
    messagesEndRef.current?.scrollIntoView({
      behavior: isStreaming ? 'instant' : 'smooth',
      block: 'end',
    })
  }, [turns, streamingContent, streamingThinking, streamingToolCalls, workflowStatus])

  const handleSubmit = async (
    content: string,
    attachments: PendingAttachment[] = []
  ) => {
    if (workflowStatus === 'submitting' || workflowStatus === 'streaming') return

    // The wrap-up workflow's first POST has empty content AND no
    // attachments — the agent opens with its calibrated greeting per
    // wrapup/micro.md. In default mode the user must type something
    // OR attach a file; in workflow modes the auto-greet effect can
    // fire with both empty so we don't gate that path.
    if (!content.trim() && !attachments.length && chatMode === 'default') return

    if (content || attachments.length) {
      // Surface "(attachments only)" in the transcript when the user
      // sent files without typing — mirrors the plugin's behavior so
      // the chat doesn't look like a blank user turn.
      const userTurnText =
        content || (attachments.length ? '(attachments only)' : '')
      appendUserTurn(userTurnText)
    }
    startStreaming()

    try {
      await streamConcierge(
        {
          content,
          session_id: sessionId, // null lazy-creates a new session
          // Per-mode context. Each workflow's endpoint requires the
          // bits it cares about (wrapup needs block_path; review needs
          // variant + optional project_path; penn_review needs
          // date_from + date_to) — passing extras is harmless because
          // Pydantic ignores unknown fields, and api.ts strips out
          // irrelevant ones for the penn_review body shape.
          block_path: modeContext?.blockPath ?? null,
          project_path: modeContext?.projectPath ?? null,
          variant: modeContext?.reviewVariant,
          date_from: modeContext?.dateFrom,
          date_to: modeContext?.dateTo,
          context: modeContext?.contextNote,
          write_to_vault: modeContext?.writeToVault,
          // Autonomous-first wrap-up: when the calendar link opened this
          // wrap-up (autoRun), the auto-greet (empty-content) first turn runs
          // the agent autonomously — it reviews evidence, writes the outcome +
          // wrap-up card + CONCIERGE.md, and archives, all streamed. As soon
          // as the user types (content non-empty), headless is false and it
          // continues as a normal interactive conversation on the same
          // session, so turn 2+ has the autonomous run's context.
          headless:
            chatMode === 'wrapup' &&
            !!modeContext?.autoRun &&
            !content.trim() &&
            !attachments.length,
          // Attachments are pre-validated client-side (5 MB cap, 10
          // count cap, mime allowlist); we forward the structured
          // payload as-is. Empty array gets stripped by the api
          // client so it doesn't show up in the JSON body.
          attachments: attachments.length
            ? attachments.map((a) => ({
                kind: a.kind,
                mime: a.mime,
                filename: a.filename,
                data_base64: a.data_base64,
              }))
            : undefined,
        },
        {
          onThinkingStart: () => {
            /* state already initialized in startStreaming */
          },
          onThinkingDelta: (d) => appendThinking(d),
          onThinkingComplete: (final) => finishThinking(final),
          onToolCall: (ev) => registerToolCall(ev),
          onToolResult: (ev) => {
            registerToolResult(ev)
            // Phase 1 of the advance trigger: flag this turn if a terminal
            // tool succeeded. The actual advance fires on onComplete below
            // so the agent's full turn (subsequent tools + final response
            // text) completes first.
            const ok = ev.ok !== false
            noteTerminalToolResult(ev.name, ok)
          },
          onContentDelta: (d) => appendStreamingContent(d),
          onComplete: (ev) => {
            // Backend sends the assistant's final text in `ev.response`
            // (see session_manager/node.py). `session_id` is populated on
            // first turn (lazy-create); otherwise echoes the existing id.
            const content = ev.response || ''
            const newSessionId = ev.session_id
            finishStreaming({ content, sessionId: newSessionId })
            // Refresh the sessions list so the picker shows the new
            // session (or the updated `last_message_at` on an existing one).
            // Only refresh in default mode — workflow sessions are kept
            // out of the default picker so the list stays focused on
            // open-ended conversation, not mode runs.
            if (chatMode === 'default') {
              loadSessions()
            }
            // Phase 2 of the walk advance: if this turn flagged a terminal
            // tool (archive_block / reschedule_block / archive_project),
            // advance now that the full turn has completed.
            maybeAdvanceOnComplete()
          },
          onError: (msg) => setError(msg),
          // Penn Review emits per-workstream + final events outside the
          // shared SSE vocabulary. Synthesize tool-pill rows so the user
          // sees per-workstream progress in the existing rendering — the
          // alternative is a 60-90s silent gap while parallel agents grind.
          onCustomEvent: (ev) => {
            if (ev.type === 'penn_workstream_done') {
              const wsName = String(ev.workstream || '?')
              const progress = (ev.progress as { done: number; total: number } | undefined) || undefined
              const isEmpty = Boolean(ev.is_empty)
              const ok = Boolean(ev.ok ?? true)
              const errorMsg = ev.error ? String(ev.error) : undefined
              const summaryBits: string[] = []
              if (progress) summaryBits.push(`${progress.done}/${progress.total}`)
              if (isEmpty) summaryBits.push('no material activity')
              else if (errorMsg) summaryBits.push(`error: ${errorMsg}`)
              else summaryBits.push('done')
              registerToolCall({ type: 'concierge_tool_call', name: wsName, input: {} })
              registerToolResult({
                type: 'concierge_tool_result',
                name: wsName,
                ok,
                summary: summaryBits.join(' · '),
              })
            } else if (ev.type === 'penn_review_done') {
              // Surface the writer outcome as a final pill. The card text
              // itself comes through onContentDelta + session_complete.
              if (ev.written_path) {
                const filename = String(ev.written_path).split('/').pop() || 'card'
                registerToolCall({ type: 'concierge_tool_call', name: 'wrote card', input: {} })
                registerToolResult({
                  type: 'concierge_tool_result',
                  name: 'wrote card',
                  ok: true,
                  summary: filename,
                })
              } else if (ev.write_error) {
                registerToolCall({ type: 'concierge_tool_call', name: 'wrote card', input: {} })
                registerToolResult({
                  type: 'concierge_tool_result',
                  name: 'wrote card',
                  ok: false,
                  summary: `write failed: ${String(ev.write_error)}`,
                })
              }
            }
          },
        },
        chatMode
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An error occurred')
    }
  }

  // Auto-greet on mode entry: when the user enters a workflow mode and
  // we don't yet have a session for it, fire a turn with empty content.
  // The wrap-up / prep / review nodes' system prompts produce their
  // calibrated opener so the chat doesn't feel blank. The dependency
  // list intentionally watches sessionId, not turns — once the session
  // is minted, this effect must NOT re-fire on subsequent user turns.
  //
  // `autoGreetArmed` is the guard against a page reload re-running a
  // STALE block: rehydrating a persisted wrap-up mode satisfies the
  // (mode !== default && !sessionId && turns === 0) shape too, and the
  // old code fired anyway — silently re-wrapping (and, with persisted
  // autoRun, headlessly archiving) whatever block was last in
  // localStorage. Only enterMode arms the flag, so the greet now fires
  // exactly once per intentional, in-session mode entry.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      autoGreetArmed &&
      chatMode !== 'default' &&
      !sessionId &&
      turns.length === 0
    ) {
      disarmAutoGreet()
      handleSubmit('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMode, sessionId, autoGreetArmed])

  // ESC exits the current workflow mode. Mirrors the modal-close
  // affordance the Obsidian plugin gets for free.
  useEffect(() => {
    if (chatMode === 'default') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitMode()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [chatMode, exitMode])

  const isStreaming = workflowStatus === 'streaming'
  // Show the live streaming block when we have any in-flight signal —
  // thinking, tool calls, or streamed content. We DON'T pre-render an
  // empty bubble just because status is "streaming" — that flickers.
  const hasLiveSignal =
    streamingThinking.length > 0 ||
    streamingToolCalls.length > 0 ||
    streamingContent.length > 0

  // When in a workflow mode, wrap the messages container in a
  // `.concierge-mode-active` element with `data-mode` set. That single
  // hook drives the per-mode accent CSS rules — keep the JSX simple,
  // let stylesheets fan it out.
  const modeWrapperClass = chatMode === 'default' ? '' : 'concierge-mode-active'

  return (
    <div className={`h-full flex flex-col ${modeWrapperClass}`} data-mode={chatMode}>
      {/* Sticky workflow banner — renders only outside default mode */}
      <ConciergeModeBanner />

      {/* Messages area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 scroll-touch overscroll-contain">
        <div className="max-w-[680px] mx-auto py-8">
          {turns.length === 0 && !isStreaming ? (
            <div className="text-center py-16">
              <h2 className="text-base text-foreground mb-2">
                What's on your mind?
              </h2>
              <p className="text-[13px] text-muted-foreground max-w-md mx-auto leading-relaxed">
                Ask about your blocks, your projects, or what's next. The Concierge has access to your vault, calendar, and pattern memory.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {turns.map((turn, i) => {
                // Live = most-recent assistant turn AND nothing currently
                // streaming on top of it. Historical and superseded cards
                // are still rendered (so the chat thread stays visually
                // consistent — no "revert to raw text" regression) but
                // their controls are frozen.
                const isLive =
                  turn.role === 'assistant' &&
                  i === turns.length - 1 &&
                  !isStreaming
                // Reveal which option was chosen on a historical card by
                // looking at the user turn that came immediately after.
                // Cards self-track `chosen` while mounted, but a page
                // reload wipes that — the next-user-turn match recovers
                // it deterministically.
                const nextUserContent =
                  turn.role === 'assistant' &&
                  i + 1 < turns.length &&
                  turns[i + 1].role === 'user'
                    ? turns[i + 1].content
                    : undefined
                return (
                  <ConciergeChatMessage
                    key={i}
                    turn={turn}
                    isLive={isLive}
                    nextUserContent={nextUserContent}
                    onStage={stageInComposer}
                    onSend={(text) => handleSubmit(text)}
                  />
                )
              })}

              {/* Live in-flight assistant turn — appears below the last
                  posted turn while streaming. Disappears the instant
                  finishStreaming pins it into `turns`. */}
              {isStreaming && hasLiveSignal && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] py-2 concierge-assistant-accent">
                    {/* Render order: tool pills (what was gathered) →
                        thinking (the reasoning) → content (the output).
                        Mirrors the cognitive flow the reader expects: data
                        first, then synthesis, then result. Penn Review
                        especially benefits — workstream-completion pills
                        accumulate during the parallel-agent phase, then
                        the consolidator's thinking explains what it's
                        dropping / merging, then the card streams in. */}
                    {streamingToolCalls.length > 0 && (
                      <div className="mb-3 space-y-1">
                        {streamingToolCalls.map((c) => (
                          <ConciergeToolPill key={c.id} call={c} />
                        ))}
                      </div>
                    )}
                    {streamingThinking && (
                      <ConciergeThinkingBlock
                        thinking={streamingThinking}
                        isStreaming
                      />
                    )}
                    {streamingContent && (
                      <div className="concierge-prose text-[15px] leading-relaxed whitespace-pre-wrap">
                        {streamingContent}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pure "thinking dots" when streaming but nothing yet to show. */}
              {isStreaming && !hasLiveSignal && (
                <div className="flex justify-start">
                  <div className="py-2 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-muted-foreground/50 animate-pulse" />
                    <span
                      className="w-1 h-1 rounded-full bg-muted-foreground/50 animate-pulse"
                      style={{ animationDelay: '150ms' }}
                    />
                    <span
                      className="w-1 h-1 rounded-full bg-muted-foreground/50 animate-pulse"
                      style={{ animationDelay: '300ms' }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex-shrink-0 max-w-[680px] mx-auto w-full px-5">
          <div className="text-[12px] text-red-500/90 py-2">
            {error}
          </div>
        </div>
      )}

      {/* Input area — hairline top border separator, no full input border */}
      <div className="flex-shrink-0 border-t border-border">
        <div className="max-w-[680px] mx-auto px-5 py-3">
          <ConciergeChatInput
            onSubmit={handleSubmit}
            disabled={isStreaming || workflowStatus === 'submitting'}
            placeholder={inputPlaceholder(chatMode, turns.length)}
            value={composerValue}
            onValueChange={setComposerValue}
            focusSignal={composerFocusSignal}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Per-mode input placeholder. Tells the user what the agent expects in
 * THIS room — wrap-up wants the outcome, prep wants the brief, review
 * wants the window. In default mode we keep the original empty-state
 * vs. continuing-conversation prompts.
 */
function inputPlaceholder(mode: string, turnCount: number): string {
  if (mode === 'wrapup') return 'Tell the Concierge how it went…'
  if (mode === 'prep') return 'What are you about to do?'
  if (mode === 'review') return 'Ask about the window, or just hit send…'
  return turnCount === 0 ? "What's on your mind?" : 'Talk to the Concierge…'
}
